/**
 * useTherapistChat Hooks
 *
 * React hooks for managing freeform therapist chat threads with streaming support.
 * Provides state management for thread CRUD, SSE streaming responses, and message history.
 *
 * Key responsibilities:
 * - List and fetch therapist chat threads via React Query
 * - Create, update, and delete threads with cache invalidation
 * - Stream AI responses token-by-token with SSE
 * - Manage streaming state (loading, content, errors, tool use)
 */

import { useState, useCallback, useRef } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  listThreads,
  getThread,
  createThread,
  deleteThread,
  updateThreadTitle,
  sendMessageStreaming,
} from "../services/therapistChat";
import type {
  TherapistThreadCreate,
  TherapistThreadResponse,
  TherapistThreadListResponse,
  TherapistStreamEvent,
} from "../types/therapistChat";

const THREADS_KEY = ["therapistThreads"] as const;
const threadKey = (id: string) => ["therapistThread", id] as const;

/**
 * State for an active streaming response
 */
export interface StreamingState {
  isStreaming: boolean;
  content: string;
  error: string | null;
  pendingUserMessage: string | null;
  activeToolName: string | null;
  /** Last message that failed — used for retry. Cleared on successful send. */
  failedMessage: string | null;
}

const INITIAL_STREAMING_STATE: StreamingState = {
  isStreaming: false,
  content: "",
  error: null,
  pendingUserMessage: null,
  activeToolName: null,
  failedMessage: null,
};

/**
 * Hook to list therapist chat threads
 */
export function useTherapistThreads() {
  return useQuery<TherapistThreadListResponse, Error>({
    queryKey: [...THREADS_KEY],
    queryFn: () => listThreads(),
    staleTime: 30 * 1000,
  });
}

/**
 * Hook to fetch a single therapist chat thread with messages
 */
export function useTherapistThread(threadId: string | null) {
  return useQuery<TherapistThreadResponse, Error>({
    queryKey: threadId ? [...threadKey(threadId)] : ["therapistThread", null],
    queryFn: () => getThread(threadId!),
    enabled: !!threadId,
    staleTime: 10 * 1000,
  });
}

/**
 * Hook to create a new therapist chat thread
 */
export function useCreateTherapistThread() {
  const queryClient = useQueryClient();

  return useMutation<TherapistThreadResponse, Error, TherapistThreadCreate>({
    mutationFn: (data) => createThread(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [...THREADS_KEY] });
    },
  });
}

/**
 * Hook to delete a therapist chat thread
 */
export function useDeleteTherapistThread() {
  const queryClient = useQueryClient();

  return useMutation<void, Error, string>({
    mutationFn: (threadId) => deleteThread(threadId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [...THREADS_KEY] });
    },
  });
}

/**
 * Hook to update a thread's title
 */
export function useUpdateThreadTitle() {
  const queryClient = useQueryClient();

  return useMutation<
    TherapistThreadResponse,
    Error,
    { threadId: string; title: string }
  >({
    mutationFn: ({ threadId, title }) => updateThreadTitle(threadId, title),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: [...THREADS_KEY] });
      queryClient.invalidateQueries({
        queryKey: [...threadKey(data.id)],
      });
    },
  });
}

/**
 * Hook for streaming therapist chat messages
 *
 * Manages the state of streaming responses including content accumulation,
 * tool use tracking, and error handling. Invalidates the thread query
 * when streaming completes so the full message history is refetched.
 */
export function useTherapistMessageStreaming(threadId: string | null) {
  const queryClient = useQueryClient();
  const [streamingState, setStreamingState] = useState<StreamingState>(
    INITIAL_STREAMING_STATE
  );

  // Track abort controller for cancellation
  const abortControllerRef = useRef<AbortController | null>(null);

  /**
   * Send a message and stream the response
   */
  const sendMessage = useCallback(
    async (content: string) => {
      if (!threadId) return;

      // Reset state and track pending user message
      setStreamingState({
        isStreaming: true,
        content: "",
        error: null,
        pendingUserMessage: content,
        activeToolName: null,
        failedMessage: null,
      });

      // Create abort controller for cancellation
      const controller = new AbortController();
      abortControllerRef.current = controller;

      let accumulatedContent = "";

      try {
        await sendMessageStreaming(
          threadId,
          { content },
          (event: TherapistStreamEvent) => {
            switch (event.type) {
              case "content":
                accumulatedContent += event.content || "";
                setStreamingState((prev) => ({
                  ...prev,
                  content: accumulatedContent,
                }));
                break;

              case "tool_use":
                setStreamingState((prev) => ({
                  ...prev,
                  activeToolName: event.tool_name || null,
                }));
                break;

              case "tool_result":
                setStreamingState((prev) => ({
                  ...prev,
                  activeToolName: null,
                }));
                break;

              case "done":
                // Streaming complete — state will be cleared below
                break;

              case "error":
                throw new Error(event.error || "Streaming error");
            }
          },
          controller.signal
        );

        // Mark streaming as complete and clear pending state
        setStreamingState(INITIAL_STREAMING_STATE);

        // Invalidate thread query to refetch full message history
        queryClient.invalidateQueries({
          queryKey: [...threadKey(threadId)],
        });

        // Also invalidate thread list (message count / updated_at may change)
        queryClient.invalidateQueries({
          queryKey: [...THREADS_KEY],
        });
      } catch (error) {
        // Don't treat abort as an error
        if (error instanceof DOMException && error.name === "AbortError") {
          setStreamingState((prev) => ({
            ...prev,
            isStreaming: false,
            pendingUserMessage: null,
            activeToolName: null,
            error: "Streaming cancelled",
            failedMessage: content,
          }));
          return;
        }

        setStreamingState((prev) => ({
          ...prev,
          isStreaming: false,
          pendingUserMessage: null,
          activeToolName: null,
          error: error instanceof Error ? error.message : "Streaming failed",
          failedMessage: content,
        }));
      } finally {
        abortControllerRef.current = null;
      }
    },
    [threadId, queryClient]
  );

  /**
   * Cancel ongoing streaming
   */
  const cancelStreaming = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
  }, []);

  /**
   * Clear error state (e.g., after user dismisses)
   */
  const clearError = useCallback(() => {
    setStreamingState((prev) => ({ ...prev, error: null }));
  }, []);

  return {
    streamingState,
    sendMessage,
    cancelStreaming,
    clearError,
  };
}
